import { environment } from '../../../environments/environment';

const BASE = environment.apiBaseUrl;

export const API = {
  auth: {
    login: `${BASE}/api/v1/auth/login`,
    logout: `${BASE}/api/v1/auth/logout`,
  },
  organizationPermission: {
    users: `${BASE}/api/v1/organization-permission/users`,
    user: (userId: number) =>
      `${BASE}/api/v1/organization-permission/users/${userId}`,
    resetPassword: (userId: number) =>
      `${BASE}/api/v1/organization-permission/users/${userId}/password`,
  },
  groupSettings: {
    groups: `${BASE}/api/v1/organization-group/groups`,
    group: (groupId: number) =>
      `${BASE}/api/v1/organization-group/groups/${groupId}`,
    groupUsers: (groupId: number) =>
      `${BASE}/api/v1/organization-group/groups/${groupId}/users`,
    groupUser: (groupId: number, userId: number) =>
      `${BASE}/api/v1/organization-group/groups/${groupId}/users/${userId}`,
  },
  spaces: {
    list: `${BASE}/api/v1/spaces`,
    create: `${BASE}/api/v1/spaces`,
    show: (spaceId: number) => `${BASE}/api/v1/spaces/${spaceId}`,
    update: (spaceId: number) => `${BASE}/api/v1/spaces/${spaceId}`,
    delete: (spaceId: number) => `${BASE}/api/v1/spaces/${spaceId}`,
    duplicate: (spaceId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/duplicate`,
    listMembers: (spaceId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/members`,
    addMembers: (spaceId: number) => `${BASE}/api/v1/spaces/${spaceId}/members`,
    removeMember: (spaceId: number, memberId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/members/${memberId}`,
    updateMemberPermission: (spaceId: number, resourcePermissionId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/members/${resourcePermissionId}/permissions`,
    contents: (spaceId: number) => `${BASE}/api/v1/spaces/${spaceId}/contents`,
    assignees: `${BASE}/api/v1/spaces/assignees`,
  },
  folders: {
    list: (spaceId: number) => `${BASE}/api/v1/spaces/${spaceId}/folders`,
    create: (spaceId: number) => `${BASE}/api/v1/spaces/${spaceId}/folders`,
    show: (spaceId: number, folderId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}`,
    update: (spaceId: number, folderId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}`,
    delete: (spaceId: number, folderId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}`,
    duplicate: (spaceId: number, folderId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}/duplicate`,
    listMembers: (spaceId: number, folderId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}/members`,
    assignMember: (spaceId: number, folderId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}/members`,
    addMembers: (spaceId: number, folderId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}/members`,
    updateMemberPermission: (spaceId: number, folderId: number, rpId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}/members/${rpId}/permissions`,
    removeMember: (spaceId: number, folderId: number, rpId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}/members/${rpId}`,
    assignees: `${BASE}/api/v1/folders/assignees`,
  },
  // Tables are a frontend rendering of Tasks; backend endpoints use `/tasks`.
  // Paths follow `specs/007-table-management/contracts/api.yaml` 1:1.
  tables: {
    list: (spaceId: number) => `${BASE}/api/v1/spaces/${spaceId}/tasks`,
    create: (spaceId: number) => `${BASE}/api/v1/spaces/${spaceId}/tasks`,
    show: (tableId: number) => `${BASE}/api/v1/tasks/${tableId}`,
    update: (tableId: number) => `${BASE}/api/v1/tasks/${tableId}`,
    delete: (tableId: number) => `${BASE}/api/v1/tasks/${tableId}`,
    duplicate: (tableId: number) => `${BASE}/api/v1/tasks/${tableId}/duplicate`,
    viewConfig: (tableId: number) =>
      `${BASE}/api/v1/tasks/${tableId}/view-config`,
    members: (tableId: number) => `${BASE}/api/v1/tasks/${tableId}/members`,
    addMembers: (tableId: number) => `${BASE}/api/v1/tasks/${tableId}/members`,
    updateMemberPermission: (tableId: number, resourcePermissionId: number) =>
      `${BASE}/api/v1/tasks/${tableId}/members/${resourcePermissionId}/permissions`,
    removeMember: (tableId: number, resourcePermissionId: number) =>
      `${BASE}/api/v1/tasks/${tableId}/members/${resourcePermissionId}`,
    listByFolder: (spaceId: number, folderId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}/tasks`,
    createInFolder: (spaceId: number, folderId: number) =>
      `${BASE}/api/v1/spaces/${spaceId}/folders/${folderId}/tasks`,
  },
  taskFields: {
    list: (taskId: number) => `${BASE}/api/v1/tasks/${taskId}/fields`,
    create: (taskId: number) => `${BASE}/api/v1/tasks/${taskId}/fields`,
    show: (taskId: number, fieldId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/fields/${fieldId}`,
    update: (taskId: number, fieldId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/fields/${fieldId}`,
    delete: (taskId: number, fieldId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/fields/${fieldId}`,
    duplicate: (taskId: number, fieldId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/fields/${fieldId}/duplicate`,
  },
  cells: {
    create: (taskId: number) => `${BASE}/api/v1/tasks/${taskId}/task-fields`,
    update: (taskId: number, taskFieldId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/task-fields/${taskFieldId}`,
    delete: (taskId: number, taskFieldId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/task-fields/${taskFieldId}`,
  },
  rows: {
    bulkDelete: (taskId: number) => `${BASE}/api/v1/tasks/${taskId}/rows`,
    delete: (taskId: number, rowNum: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}`,
    reorder: (taskId: number, rowNum: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/reorder`,
    duplicate: (taskId: number, rowNum: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/duplicate`,
    /** GET a single row with hierarchy metadata (depth, parent_chain). */
    show: (taskId: number, rowNum: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}`,
    /** GET/POST subtasks of a row (feature 013). */
    subtasks: (taskId: number, rowNum: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/subtasks`,
  },
  activity: {
    list: (taskId: number, rowNum: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/activity`,
    create: (taskId: number, rowNum: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/activity`,
    show: (taskId: number, rowNum: number, commentId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/activity/${commentId}`,
    update: (taskId: number, rowNum: number, commentId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/activity/${commentId}`,
    delete: (taskId: number, rowNum: number, commentId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/activity/${commentId}`,
    replies: (taskId: number, rowNum: number, commentId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/activity/${commentId}/replies`,
    like: (taskId: number, rowNum: number, commentId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/activity/${commentId}/like`,
    uploadAttachment: (taskId: number, rowNum: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/activity/attachments`,
    deleteAttachment: (taskId: number, rowNum: number, attachmentId: number) =>
      `${BASE}/api/v1/tasks/${taskId}/rows/${rowNum}/activity/attachments/${attachmentId}`,
  },
  organizationPermissionExtended: {
    searchUsersWithSpaceSelected: `${BASE}/api/v1/organization-permission/users`,
    searchGroupsWithSpaceSelected: `${BASE}/api/v1/organization-permission/groups`,
  },
  importedFiles: {
    list: `${BASE}/api/v1/imported-files`,
    upload: `${BASE}/api/v1/uploads`,
    deleteUpload: (uploadId: number | string) =>
      `${BASE}/api/v1/uploads/${uploadId}`,
    commit: `${BASE}/api/v1/imported-files/commit`,
    show: (fileId: number) => `${BASE}/api/v1/imported-files/${fileId}`,
    updateColumn: (fileId: number, columnIndex: number) =>
      `${BASE}/api/v1/imported-files/${fileId}/columns/${columnIndex}`,
    migrate: (fileId: number) =>
      `${BASE}/api/v1/imported-files/${fileId}/migrate`,
  },
} as const;
